import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { ThemeProvider } from "@mui/material/styles";
import { Box, CircularProgress, Typography } from "@mui/material";
import { useSelector, useDispatch } from "react-redux";
import apiClient, {
  getTenantConfig,
  updateTenantConfig,
  setActiveTenant as setApiTenant,
  resolveTenantByDomain,
} from "../api/apiClient";

import { setCurrentUser } from "../utils/auth";
import { isAuthenticated } from "../utils/auth";
import {
  logout as logoutAction,
  restoreUser,
  setActiveTenant as setActiveTenantAction,
  forceSetActiveTenant,
} from "../store/authSlice";
import { createAppTheme } from "../theme";
import TenantNotConfiguredScreen from "../components/TenantNotConfiguredScreen";

const IS_MOCK = process.env.REACT_APP_LOGIN_MODE === "mock";

export const TenantContext = createContext();

const FEATURE_ALIASES = {
  agency: "agencyManagement",
  certifications: "certManagement",
  certs: "certManagement",
  trainingCenter: "publicClassListing",
  trainingCenterClassListing: "classes",
  publicClasses: "publicClassListing",
  instructorContent: "instructorClassContent",
};

function resolveFeatureName(name) {
  return FEATURE_ALIASES[name] || name;
}

export function useTenant() {
  return useContext(TenantContext);
}

// ── Splash / loading screen ───────────────────────────────────────────────────
// Plain inline-CSS so it works before or inside any ThemeProvider.
function SplashScreen({ message, icon, primaryColor = "#003876" }) {
  return (
    <Box
      sx={{
        position: "fixed",
        inset: 0,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        bgcolor: "#f7f9fb",
        gap: 2,
        zIndex: 9999,
      }}
    >
      {icon && (
        <Box
          component="img"
          src={icon}
          alt=""
          sx={{ width: 52, height: 52, objectFit: "contain", mb: 1, borderRadius: "12px" }}
        />
      )}
      <CircularProgress
        size={36}
        thickness={3}
        sx={{ color: primaryColor }}
      />
      {message && (
        <Typography
          sx={{ fontSize: 13, color: "#7B8499", mt: 0.5, fontFamily: "Inter, sans-serif" }}
        >
          {message}
        </Typography>
      )}
    </Box>
  );
}

export function TenantProvider({ children }) {
  console.debug('[TC:boot] TenantProvider — mount');
  const dispatch = useDispatch();
  const authState = useSelector((state) => state.auth);

  const [tenantConfig, setTenantConfig] = useState(null);
  // viewAs: null = real role, "STUDENT" = instructor viewing as student
  const [viewAs, setViewAs] = useState(null);
  // domainTenant: resolved from the browser hostname in real mode
  const [domainTenant, setDomainTenant] = useState(null);
  const [domainResolved, setDomainResolved] = useState(IS_MOCK);
  const [domainNotFound, setDomainNotFound] = useState(false);

  const activeTenantId = authState.activeTenantId;
  const isGlobalAdmin = authState.roles?.includes("GLOBAL_ADMIN");
  const isImpersonating = !!authState.impersonating;

  // Derive the MUI theme from the best available source — no separate state needed.
  // Priority: full tenant config (post-login) > domain tenant (pre-login) > default.
  // Global admins not impersonating always use default typography so font doesn't
  // change when switching tenants in the admin bar.
  const theme = useMemo(() => {
    const stripTypography = (cfg) =>
      cfg ? { ...cfg, typography: undefined } : cfg;
    if (tenantConfig && Object.keys(tenantConfig).length > 0) {
      const cfg = isGlobalAdmin && !isImpersonating ? stripTypography(tenantConfig) : tenantConfig;
      return createAppTheme(cfg);
    }
    if (domainTenant?.type === "tenant") {
      return createAppTheme(domainTenant);
    }
    return createAppTheme();
  }, [tenantConfig, domainTenant, isGlobalAdmin, isImpersonating]);

  // Bridge the resolved MUI theme's tenant colors onto the Beacon CSS custom
  // properties so Beacon-styled components stay tenant-color-aware instead of
  // hardcoding Beacon's own brand palette. --color-accent-violet-mid is left
  // un-bridged (stays the fixed Ink Violet token) — it's used as the pervasive
  // muted-caption/label color throughout Beacon components, and tying it to the
  // tenant's secondary color made every caption tint toward that tenant's accent.
  useEffect(() => {
    const root = document.documentElement.style;
    // Chrome colors are pinned to the Beacon tokens (demo spec: ink primary
    // everywhere). Only the tenant's brand color is bridged, as a decorative
    // accent for logos/avatars — never as the UI action color.
    root.removeProperty('--color-primary');
    root.removeProperty('--color-accent-violet');
    root.removeProperty('--color-accent-violet-deep');
    root.setProperty('--color-tenant', theme.palette.tenant?.main || theme.palette.primary.main);
  }, [theme]);

  // In real mode, resolve the current hostname on mount.
  // Backend returns { type:"public", tenants:[...] } for the public listing domain,
  // { type:"tenant", id, name, theme, ... } for a mapped tenant domain, or null for unknown.
  useEffect(() => {
    if (IS_MOCK) return;
    const hostname = window.location.hostname;
    if (hostname === "localhost" || hostname === "127.0.0.1") {
      setDomainResolved(true);
      return;
    }
    resolveTenantByDomain(hostname)
      .then((result) => {
        if (result) {
          setDomainTenant(result);
          console.debug("[TC:boot] domain resolved —", hostname, "→", result.type, result.id || "(public)");
        } else {
          setDomainNotFound(true);
        }
      })
      .finally(() => setDomainResolved(true));
  }, []);

  // Keep the apiClient interceptor in sync. Deliberately NOT a useEffect: effects fire
  // bottom-up (children before parents), so a child page's mount-time data fetch could
  // read the stale/unset tenant header on the exact render where activeTenantId first
  // becomes available. Setting this module-level value directly during render runs
  // before any child mounts/effects, regardless of commit order, and is safe since it's
  // an idempotent assignment to an external singleton, not React state.
  {
    const domainTenantId = domainTenant?.type === "tenant" ? String(domainTenant.id) : null;
    setApiTenant(activeTenantId || domainTenantId);
  }

  // Merge global roles with active tenant roles.
  const globalRoles = Array.isArray(authState.roles) ? authState.roles : [];
  const activeTenantRoles =
    activeTenantId && Array.isArray(authState.tenantRoles?.[activeTenantId])
      ? authState.tenantRoles[activeTenantId]
      : [];
  const mergedRoles = [...new Set([...globalRoles, ...activeTenantRoles])];

  const user =
    authState.isAuthenticated && authState.userId
      ? {
          id: authState.userId,
          username: authState.username,
          email: authState.email,
          fullName: authState.fullName || null,
          roles: mergedRoles,
          tenantId: activeTenantId,
          tenantRoles: authState.tenantRoles || {},
          agencyId: authState.agencyId || null,
          agencyName: authState.agencyName || null,
        }
      : null;

  // On mount, restore session from cookie if needed.
  useEffect(() => {
    const hasCookie = isAuthenticated();
    console.debug('[TC:boot] auth/me effect — hasCookie:', hasCookie, 'userId:', authState.userId, 'loading:', authState.loading);
    if (hasCookie && !authState.userId && !authState.loading) {
      console.debug('[TC:boot] fetching /api/v1/auth/me to restore session');
      apiClient
        .get("/api/v1/auth/me")
        .then((res) => {
          const data = res.data;
          console.debug('[TC:boot] /api/v1/auth/me success — userId:', data.id);
          dispatch(
            restoreUser({
              userId: data.id,
              tenantRoles: data.tenantRoles || {},
              email: data.email,
              username: data.username,
              fullName: data.fullName || null,
              agencyId: data.agencyId || null,
              agencyName: data.agencyName || null,
              roles: data.roles || [],
            }),
          );
          const firstTenant = Object.keys(data.tenantRoles || {})[0] || null;
          setCurrentUser({
            id: data.id,
            username: data.username,
            email: data.email,
            roles: data.roles || [],
            tenantId: firstTenant,
            tenantRoles: data.tenantRoles || {},
          });
        })
        .catch((err) => {
          console.error('[TC:boot] /api/v1/auth/me failed:', err?.response?.status, err?.message);
          dispatch(logoutAction());
        });
    }
    // Mount-only session restore; re-running on authState changes this effect itself causes would refetch needlessly.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load tenant config whenever the active tenant changes.
  useEffect(() => {
    console.debug('[TC:boot] tenantConfig effect — user:', !!user, 'activeTenantId:', activeTenantId);
    if (user) {
      if (activeTenantId) {
        console.debug('[TC:boot] fetching tenant config for tenantId:', activeTenantId);
        getTenantConfig(activeTenantId)
          .then((config) => {
            console.debug('[TC:boot] tenant config loaded:', config?.name);
            setTenantConfig(config ?? {});
          })
          .catch((err) => {
            console.error('[TC:boot] getTenantConfig failed:', err?.response?.status, err?.message);
            setTenantConfig({});
          });
      } else {
        setTenantConfig({});
      }
    } else {
      setTenantConfig(null);
    }
    // `user` is a new object every render; depend on the primitive id instead to avoid refetching on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authState.userId, activeTenantId]);

  const login = (u) => {
    setCurrentUser(u);
  };

  const logout = () => {
    // Best-effort: clears the httpOnly session cookie and revokes the
    // server-side session record. Local state is cleared regardless of
    // whether this call succeeds (e.g. token already expired).
    apiClient.post("/api/v1/auth/logout").catch(() => {});
    dispatch(logoutAction());
    setCurrentUser(null);
    setTenantConfig(null);
  };

  const switchTenant = (tenantId) => {
    const isGlobalAdmin = authState.roles?.includes("GLOBAL_ADMIN");
    if (isGlobalAdmin || !authState.tenantRoles?.[tenantId]) {
      dispatch(forceSetActiveTenant(tenantId));
    } else {
      dispatch(setActiveTenantAction(tenantId));
    }
  };

  const saveTenantConfig = (changes) => {
    if (!activeTenantId) return Promise.reject(new Error("no tenant"));
    return updateTenantConfig(activeTenantId, changes).then((cfg) => {
      setTenantConfig(cfg);
      return cfg;
    });
  };

  const isFeatureEnabled = (name) => {
    if (Array.isArray(name)) {
      return name.every((n) => isFeatureEnabled(n));
    }
    if (!tenantConfig || typeof tenantConfig.features === "undefined") {
      return true;
    }
    const featureName = resolveFeatureName(name);
    return !!(tenantConfig.features[featureName] || tenantConfig.features[name]);
  };

  const effectiveRoles = viewAs
    ? [viewAs]
    : Array.isArray(user?.roles)
      ? user.roles
      : [];
  const effectiveUser = user ? { ...user, roles: effectiveRoles } : null;

  const value = {
    user,
    effectiveUser,
    tenantConfig,
    activeTenantId,
    login,
    logout,
    switchTenant,
    saveTenantConfig,
    isFeatureEnabled,
    viewAs,
    setViewAs,
    domainTenant,
    domainResolved,
  };

  // ── Domain not found — plain error screen, no theming needed ─────────────
  if (domainNotFound) {
    return <TenantNotConfiguredScreen />;
  }

  // ── Determine loading state + splash message ──────────────────────────────
  const resolvedPrimary = theme.palette.primary.main;
  const splashIcon = domainTenant?.icon || domainTenant?.logo || null;

  const isResolvingDomain = !domainResolved;
  const isRestoringSession = authState.isAuthenticated && !user;
  const isLoadingConfig = user && !tenantConfig;

  const splashMessage = isResolvingDomain
    ? "Loading your training center…"
    : isRestoringSession
      ? "Restoring your session…"
      : isLoadingConfig
        ? "Loading configuration…"
        : null;

  console.debug('[TC:boot] TenantProvider — rendering. splash:', splashMessage, 'user:', user?.username, 'tenant:', tenantConfig?.name);

  // ThemeProvider always wraps so the splash screen already uses tenant colors.
  return (
    <ThemeProvider theme={theme}>
      {splashMessage ? (
        <SplashScreen
          message={splashMessage}
          icon={splashIcon}
          primaryColor={resolvedPrimary}
        />
      ) : (
        <TenantContext.Provider value={value}>
          {children}
        </TenantContext.Provider>
      )}
    </ThemeProvider>
  );
}
